import axios, { AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { getClientId } from '../analytics/identity';
import { ensureCsrfToken, resetCsrfToken, REQUEST_TIMEOUT_MS } from './csrf';
import type { DeviceCollection } from './deviceFingerprint';
import type { CancelReasonList, DeclineContext } from './types';

/**
 * CPAYMENT-1717: the browser's stable id, the bridge between telemetry and the database. The
 * backend writes it to `external_user_request.global_client_id`, which turns "this browser → all
 * its tickets → merchants" into a plain SQL join instead of an export from Sentry.
 *
 * Never let it break the request: reading it touches `localStorage`, which can throw in a locked
 * down browser.
 */
const readClientId = (): string | undefined => {
  try {
    return getClientId();
  } catch {
    return undefined;
  }
};

const logWithTimestamp = (message?: string, ...rest: any) => {
  const timestamp = new Date().toISOString();
  console.log(`[${timestamp}] ${message}`, ...rest);
};

const isCsrfTokenRequest = (config: AxiosRequestConfig) =>
  typeof config.url === 'string' && config.url.endsWith('/api/csrf-token');

axios.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  const method = (config.method ?? 'get').toLowerCase();
  if (method !== 'post' || isCsrfTokenRequest(config)) {
    return config;
  }
  try {
    const token = await ensureCsrfToken();
    config.headers.set('X-CSRF-Token', token);
  } catch (error) {
    logWithTimestamp('Error while getting CSRF Token:', error);
  }
  return config;
});

interface CsrfRetryConfig extends InternalAxiosRequestConfig {
  _csrfRetry?: boolean;
}

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config as CsrfRetryConfig | undefined;
    if (
      error?.response?.status === 403 &&
      original &&
      !original._csrfRetry &&
      (original.method ?? 'get').toLowerCase() === 'post' &&
      !isCsrfTokenRequest(original)
    ) {
      original._csrfRetry = true;
      resetCsrfToken();
      try {
        const token = await ensureCsrfToken();
        original.headers.set('X-CSRF-Token', token);
        return axios.request(original);
      } catch (refreshError) {
        logWithTimestamp('Error refreshing CSRF token after 403:', refreshError);
      }
    }
    return Promise.reject(error);
  },
);

// `device` is sent once per page load, by the first call only; polls pass nothing.
export const getTransaction = async (
  uuid?: string | undefined,
  user_id?: string,
  device?: DeviceCollection
) => {
  try {
    const res = await axios.post(
      '/api/get-transaction',
      {
        uuid,
        user_id,
        globalClientId: readClientId(),
        ...(device ? { device } : {}),
      },
      { timeout: REQUEST_TIMEOUT_MS }
    );
    return res;
  } catch (error) {
    logWithTimestamp('Error fetching transaction:', error);
    throw error;
  }
};

// CPAYMENT-1542: client picks one of the offered deposit amounts
export const selectVariant = async (
  uuid: string | undefined,
  variantId: number
) => {
  try {
    const res = await axios.post(
      '/api/select-variant',
      { uuid, variantId },
      { timeout: REQUEST_TIMEOUT_MS }
    );
    return res;
  } catch (error) {
    logWithTimestamp('Error selecting variant:', error);
    throw error;
  }
};

// CPAYMENT-1670: client dismissed the variant offer and asks for a new requisite search
export const retrySearch = async (uuid: string | undefined) => {
  try {
    const res = await axios.post(
      '/api/retry-search',
      { uuid },
      { timeout: REQUEST_TIMEOUT_MS }
    );
    return res;
  } catch (error) {
    logWithTimestamp('Error requesting new requisite search:', error);
    throw error;
  }
};

// CPAYMENT-1716: client left the requisite screen for the receipt upload. Until this call the
// backend sees a silent ticket and expires it after 10 minutes; after it the ticket keeps its
// full window. Returns the fresh state, so the caller can pick up the new expiresAt.
//
// CPAYMENT-1753: `onePage` asks for the shorter window — 20 minutes instead of 35. The one-page
// template marks the intent the moment the payment screen opens, so the client spends the whole
// window in front of the receipt form and a 35-minute one would only hold the payout slot longer.
// How many minutes each flavour gets is the backend's call: we never send or draw the number.
export const markReceiptIntent = async (
  uuid: string | undefined,
  onePage = false
) => {
  try {
    const res = await axios.post(
      '/api/receipt-intent',
      onePage ? { uuid, onePage: true } : { uuid },
      { timeout: REQUEST_TIMEOUT_MS }
    );
    return res;
  } catch (error) {
    logWithTimestamp('Error marking receipt intent:', error);
    throw error;
  }
};

export const processTransaction = async (
  uuid: string | undefined,
  user_id?: string,
  paymentTicketHistory?: object[],
  isClientWasUnblocked?: boolean,
  isClientBlocked?: boolean
) => {
  try {
    const res = await axios.post(
      '/api/process-transaction',
      {
        uuid,
        user_id,
        paymentTicketHistory,
        isClientWasUnblocked,
        isClientBlocked,
      },
      { timeout: REQUEST_TIMEOUT_MS }
    );
    return res;
  } catch (error) {
    console.error('Error processing transaction:', error);
    throw error;
  }
};

export const declineTransaction = async (
  uuid: string | undefined,
  user_id: string,
  paymentTicketHistory: object[],
  isClientWasUnblocked: boolean,
  isClientBlocked: boolean,
  pick?: { cancelReasonId: number; cancelReasonRevisionId: number },
  declineContext?: DeclineContext
) => {
  try {
    const res = await axios.post(
      '/api/decline-transaction',
      {
        uuid,
        user_id,
        paymentTicketHistory,
        isClientWasUnblocked,
        isClientBlocked,
        ...(pick ?? {}),
        ...(declineContext ? { declineContext } : {}),
      },
      { timeout: REQUEST_TIMEOUT_MS }
    );
    return res;
  } catch (error) {
    console.error('Error declining transaction:', error);
    throw error;
  }
};

// The cancel survey is optional: a failed load means no survey, never a broken cancel.
export const fetchCancelReasons = async (uuid: string | undefined): Promise<CancelReasonList | null> => {
  try {
    const res = await axios.post('/api/cancel-reasons', { uuid }, { timeout: REQUEST_TIMEOUT_MS });
    const data = res.data?.data;
    return data && Array.isArray(data.items)
      ? { revisionId: typeof data.revisionId === 'number' ? data.revisionId : null, items: data.items }
      : null;
  } catch (error) {
    logWithTimestamp('Error loading cancel reasons:', error);
    return null;
  }
};

export const recreateTransaction = async (
  uuid: string | undefined,
  recreationPaymentMethod: string
) => {
  try {
    const res = await axios.post(
      '/api/recreate-transaction',
      { uuid, recreationPaymentMethod },
      { timeout: REQUEST_TIMEOUT_MS }
    );
    return res;
  } catch (error) {
    console.error('Error confirming transaction:', error);
    throw error;
  }
};

export const confirmTransaction = async (
  uuid: string | undefined,
  user_id: string,
  paymentTicketHistory: object[],
  isClientWasUnblocked: boolean,
  isClientBlocked: boolean
) => {
  try {
    const fields: any = {
      uuid,
      user_id,
      paymentTicketHistory,
      isClientWasUnblocked,
      isClientBlocked,
    };

    const res = await axios.post('/api/confirm-transaction', fields, {
      timeout: REQUEST_TIMEOUT_MS,
    });
    return res;
  } catch (error) {
    console.error('Error confirming transaction:', error);
    throw error;
  }
};
